import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Search, X, CornerDownLeft, Plus, Coffee } from "react-feather";

const getMenuId = (item) => item?.menu_id || item?.item_id || item?.id;

const getPrice = (item) => Number(item?.price_standard || item?.price || item?.rate || 0);

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const normalizeItemForAdd = (item) => ({
  ...item,
  menu_id: getMenuId(item),
  price_standard: getPrice(item),
});

const getItemSearchText = (item) =>
  [
    item?.name,
    item?.category_name,
    item?.category,
    item?.group_name,
    item?.menu_category,
    item?.item_code,
    item?.code,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const cancelDebounce = (fn) => {
  if (fn && typeof fn.cancel === "function") {
    fn.cancel();
  }
};

const SearchBar = ({
  searchTerm = "",
  setSearchTerm,
  debouncedSetSearchTerm,
  filteredItems = [],
  handleAddItem,
  highlightedIndex = 0,
  setHighlightedIndex,
}) => {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const baseItemsRef = useRef([]);
  const [isFocused, setIsFocused] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || "");

  // Position of the suggestions dropdown — computed from the input's bounding rect
  // so we can render the panel via portal at document.body and escape any parent
  // overflow:hidden / stacking-context traps. This was the root cause of the
  // suggestions appearing "in the background".
  const [dropdownRect, setDropdownRect] = useState(null);

  const incomingItems = useMemo(
    () => (Array.isArray(filteredItems) ? filteredItems.filter(Boolean) : []),
    [filteredItems]
  );

  useEffect(() => {
    if (!localSearchTerm) {
      baseItemsRef.current = incomingItems;
    } else if (!baseItemsRef.current.length && incomingItems.length) {
      baseItemsRef.current = incomingItems;
    }
  }, [incomingItems, localSearchTerm]);

  useEffect(() => {
    if (!isFocused && searchTerm !== localSearchTerm) {
      setLocalSearchTerm(searchTerm || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isFocused]);

  const baseItems = baseItemsRef.current.length ? baseItemsRef.current : incomingItems;
  const query = String(localSearchTerm || "").trim().toLowerCase();

  const safeItems = useMemo(() => {
    if (!query) return baseItems;
    return baseItems.filter((item) => getItemSearchText(item).includes(query));
  }, [baseItems, query]);

  const suggestions = useMemo(() => safeItems.slice(0, 7), [safeItems]);

  const showSuggestions = Boolean(localSearchTerm && isFocused && suggestions.length > 0);
  const showNoResults = Boolean(localSearchTerm && isFocused && safeItems.length === 0);

  // Measure and position the dropdown whenever it should be visible.
  const measureDropdown = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!(showSuggestions || showNoResults)) {
      setDropdownRect(null);
      return undefined;
    }

    measureDropdown();

    const handle = () => measureDropdown();
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true); // capture phase: catches scroll on any ancestor

    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [showSuggestions, showNoResults, measureDropdown, suggestions.length]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;
      // Allow clicks inside the input wrapper
      if (rootRef.current && rootRef.current.contains(target)) return;
      // Allow clicks inside the portaled dropdown (it has data-ns-search-portal)
      if (target && target.closest && target.closest("[data-ns-search-portal]")) return;
      setIsFocused(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= suggestions.length && suggestions.length > 0) {
      setHighlightedIndex?.(suggestions.length - 1);
    }
  }, [highlightedIndex, suggestions.length, setHighlightedIndex]);

  const clearParentSearch = () => {
    cancelDebounce(debouncedSetSearchTerm);
    setSearchTerm?.("");
    debouncedSetSearchTerm?.("");
  };

  const updateSearch = (value) => {
    setLocalSearchTerm(value);
    setHighlightedIndex?.(0);

    if (!value) {
      clearParentSearch();
    }
  };

  const clearSearch = () => {
    updateSearch("");
    inputRef.current?.focus();
  };

  const addItem = (item) => {
    if (!item) return;

    const normalized = normalizeItemForAdd(item);
    handleAddItem?.(normalized);

    setLocalSearchTerm("");
    baseItemsRef.current = incomingItems;
    setHighlightedIndex?.(0);
    clearParentSearch();
    setIsFocused(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (event) => {
    if (!localSearchTerm) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!suggestions.length) return;
      setHighlightedIndex?.((prev) => Math.min(Number(prev || 0) + 1, suggestions.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!suggestions.length) return;
      setHighlightedIndex?.((prev) => Math.max(Number(prev || 0) - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (!suggestions.length) return;
      addItem(suggestions[Math.min(Number(highlightedIndex || 0), suggestions.length - 1)]);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      clearSearch();
      setIsFocused(false);
    }
  };

  const currency = getCurrency();

  const renderDropdown = () => {
    if (!dropdownRect) return null;
    if (typeof document === "undefined") return null;

    const portalStyles = {
      position: "fixed",
      top: dropdownRect.top,
      left: dropdownRect.left,
      width: dropdownRect.width,
      zIndex: 2147483647, // sit above the fixed bill panel and any nav
    };

    return createPortal(
      <div data-ns-search-portal style={portalStyles}>
        {showSuggestions && (
          <div className="ns-search-suggestions">
            <div className="ns-search-suggestion-head">
              <strong>Quick add results</strong>
              <span>{suggestions.length} of {safeItems.length}</span>
            </div>

            <div className="ns-search-suggestion-list">
              {suggestions.map((item, index) => {
                const isActive = index === Number(highlightedIndex || 0);
                const menuId = getMenuId(item);

                return (
                  <button
                    key={menuId || item.name || index}
                    type="button"
                    className={`ns-search-suggestion ${isActive ? "active" : ""}`}
                    onMouseEnter={() => setHighlightedIndex?.(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      addItem(item);
                    }}
                  >
                    <div className="ns-search-suggestion-main">
                      <span className="ns-search-suggestion-icon">
                        <Coffee size={15} />
                      </span>

                      <div style={{ minWidth: 0 }}>
                        <div className="ns-search-suggestion-name">{item.name || "Unnamed Item"}</div>
                        <div className="ns-search-suggestion-sub">Tap or press Enter to add</div>
                      </div>
                    </div>

                    <span className="ns-search-suggestion-price">{currency}{money(getPrice(item))}</span>

                    <span className="ns-search-add-pill">
                      <Plus size={15} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {showNoResults && (
          <div className="ns-search-no-results">
            No matching menu items found. Try a different spelling or category.
          </div>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div ref={rootRef} className={`ns-search-shell ${isFocused ? "focused" : ""}`}>
      <style>{`
        .ns-search-shell {
          position: relative;
          width: 100%;
          margin-top: 14px;
          /* z-index no longer matters here — dropdown is portaled out */
        }

        .ns-search-box {
          position: relative;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.10);
          border-radius: 16px;
          box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
          transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
        }

        .ns-search-shell.focused .ns-search-box {
          border-color: rgba(37, 99, 235, 0.36);
          box-shadow: 0 14px 34px rgba(37, 99, 235, 0.12);
        }

        .ns-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          pointer-events: none;
          z-index: 2;
        }

        .ns-search-input {
          width: 100%;
          height: 50px;
          border: 0;
          outline: none;
          background: transparent;
          border-radius: 16px;
          padding: 0 110px 0 44px;
          color: #0f172a;
          font-size: 0.92rem;
          font-weight: 700;
        }

        .ns-search-input::placeholder {
          color: #94a3b8;
          font-weight: 600;
        }

        .ns-search-right {
          position: absolute;
          right: 9px;
          top: 50%;
          transform: translateY(-50%);
          display: inline-flex;
          align-items: center;
          gap: 7px;
        }

        .ns-search-count {
          min-width: 42px;
          height: 30px;
          border-radius: 999px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.72rem;
          font-weight: 900;
          padding: 0 9px;
          white-space: nowrap;
        }

        .ns-search-clear {
          width: 30px;
          height: 30px;
          border-radius: 11px;
          border: 0;
          background: #f1f5f9;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.16s ease;
        }

        .ns-search-clear:hover {
          background: #fee2e2;
          color: #b91c1c;
        }

        .ns-search-help {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          color: #64748b;
          font-size: 0.72rem;
          line-height: 1.4;
          margin-top: 7px;
          padding: 0 3px;
        }

        .ns-search-kbd {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          white-space: nowrap;
        }

        .ns-search-kbd span {
          min-width: 23px;
          height: 22px;
          padding: 0 6px;
          border-radius: 7px;
          background: #ffffff;
          border: 1px solid rgba(15, 23, 42, 0.10);
          color: #475569;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.68rem;
          font-weight: 900;
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.04);
        }

        /* These styles apply to the portaled dropdown too — they're global */
        .ns-search-suggestions {
          background: rgba(255, 255, 255, 0.98);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          box-shadow: 0 22px 55px rgba(15, 23, 42, 0.18);
          overflow: hidden;
        }

        .ns-search-suggestion-head {
          padding: 10px 12px;
          background:
            radial-gradient(circle at top left, rgba(37, 99, 235, 0.10), transparent 32%),
            #f8fafc;
          border-bottom: 1px solid rgba(15, 23, 42, 0.08);
          display: flex;
          justify-content: space-between;
          gap: 10px;
          align-items: center;
        }

        .ns-search-suggestion-head strong {
          color: #0f172a;
          font-size: 0.8rem;
          font-weight: 900;
        }

        .ns-search-suggestion-head span {
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 800;
        }

        .ns-search-suggestion-list {
          max-height: 315px;
          overflow-y: auto;
          padding: 6px;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-search-suggestion-list::-webkit-scrollbar {
          width: 8px;
        }

        .ns-search-suggestion-list::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 999px;
        }

        .ns-search-suggestion-list::-webkit-scrollbar-thumb {
          background: #93c5fd;
          border-radius: 999px;
        }

        .ns-search-suggestion {
          width: 100%;
          border: 0;
          background: #ffffff;
          border-radius: 13px;
          padding: 10px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto auto;
          gap: 10px;
          align-items: center;
          cursor: pointer;
          text-align: left;
          transition: background 0.14s ease, transform 0.14s ease;
        }

        .ns-search-suggestion:hover,
        .ns-search-suggestion.active {
          background: #eff6ff;
        }

        .ns-search-suggestion-icon {
          width: 32px;
          height: 32px;
          border-radius: 12px;
          background: #f8fafc;
          color: #2563eb;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
        }

        .ns-search-suggestion-main {
          display: flex;
          align-items: center;
          gap: 9px;
          min-width: 0;
        }

        .ns-search-suggestion-name {
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 900;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .ns-search-suggestion-sub {
          color: #94a3b8;
          font-size: 0.7rem;
          margin-top: 2px;
        }

        .ns-search-suggestion-price {
          color: #1d4ed8;
          font-weight: 900;
          font-size: 0.86rem;
          white-space: nowrap;
        }

        .ns-search-add-pill {
          width: 32px;
          height: 32px;
          border-radius: 12px;
          background: #1d4ed8;
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 18px rgba(37, 99, 235, 0.20);
        }

        .ns-search-no-results {
          background: #ffffff;
          border: 1px dashed rgba(100, 116, 139, 0.28);
          border-radius: 16px;
          padding: 16px;
          color: #64748b;
          font-size: 0.84rem;
          text-align: center;
          box-shadow: 0 22px 55px rgba(15, 23, 42, 0.14);
        }

        @media (max-width: 768px) {
          .ns-search-input {
            height: 46px;
            padding-right: 74px;
            font-size: 0.86rem;
          }

          .ns-search-count {
            display: none;
          }

          .ns-search-help {
            display: none;
          }

          .ns-search-suggestion {
            grid-template-columns: minmax(0, 1fr) auto;
          }

          .ns-search-add-pill {
            display: none;
          }
        }
      `}</style>

      <div className="ns-search-box">
        <Search size={18} className="ns-search-icon" />

        <input
          ref={inputRef}
          type="text"
          placeholder="Search menu items..."
          value={localSearchTerm}
          onChange={(event) => updateSearch(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={handleKeyDown}
          className="ns-search-input"
          aria-label="Search menu items"
          autoComplete="off"
        />

        <div className="ns-search-right">
          <span className="ns-search-count">{safeItems.length} found</span>
          {localSearchTerm ? (
            <button type="button" className="ns-search-clear" onClick={clearSearch} aria-label="Clear search">
              <X size={15} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="ns-search-help">
        <span>Search by item name and press Enter to add the highlighted item.</span>
        <span className="ns-search-kbd">
          <span>↑</span>
          <span>↓</span>
          <span>
            <CornerDownLeft size={12} />
          </span>
        </span>
      </div>

      {renderDropdown()}
    </div>
  );
};

export default SearchBar;